import React, { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Grid } from "@mui/material";
import axios from "../../libs/axios";
import { useLocation } from "react-router-dom";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const CHART_FONT = "Arial, Helvetica, sans-serif";
const TOWN_MAIN_COLOR = "#243859";
const SPRINKLER_COLOR = "#791664";
const HYDRANT_COLOR = "#0F3860";
const GRID_COLOR = "#d3d3d3";
// Fixed pixel size (Word doc dimensions at 96 DPI) so screenshots are never clipped by viewport
const CM_TO_PX = 96 / 2.54;
export const HYDRANT_CHART_WIDTH_PX = Math.round(14.39 * CM_TO_PX);
export const HYDRANT_CHART_HEIGHT_PX = Math.round(9.61 * CM_TO_PX);
const CHART_PADDING_PX = Math.round(0.25 * CM_TO_PX);
const CHART_INNER_WIDTH_PX = HYDRANT_CHART_WIDTH_PX - 2 * CHART_PADDING_PX;
const CHART_INNER_HEIGHT_PX = HYDRANT_CHART_HEIGHT_PX - 2 * CHART_PADDING_PX;

export default function HydrantFlowChart({ text }) {
  const { state } = useLocation();
  const [sprinklersData, setSprinklersData] = useState([]);
  const [hydrantData, setHydrantData] = useState([]);
  const [townMain, setTownMain] = useState([]);

  const options = {
    responsive: false,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 4, right: 48, bottom: 4, left: 4 },
    },
    plugins: {
      title: {
        display: true,
        text: "Hydrant Flow Test Results",
        font: {
          family: CHART_FONT,
          size: 15,
          weight: "normal",
        },
        color: "#000",
        padding: { bottom: 12 },
      },
      tooltip: {
        callbacks: {
          title: () => "",
          label: (context) =>
            `${context.parsed.x}L/min @ ${context.parsed.y}kPa`,
        },
      },
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        type: "linear",
        position: "bottom",
        min: 0,
        max: 1400,
        title: {
          display: true,
          text: "Flow (L/min)",
          font: { family: CHART_FONT, size: 12, weight: "normal" },
          color: "#000",
          padding: { top: 8 },
        },
        ticks: {
          stepSize: 200,
          maxTicksLimit: 8,
          font: { family: CHART_FONT, size: 11 },
          color: "#000",
        },
        grid: {
          display: false,
          drawOnChartArea: false,
        },
        border: {
          color: "#000",
          width: 1,
        },
      },
      y: {
        min: 0,
        max: 900,
        title: {
          display: true,
          text: "Pressure (kPa)",
          font: { family: CHART_FONT, size: 12, weight: "normal" },
          color: "#000",
          padding: { bottom: 8 },
        },
        ticks: {
          stepSize: 100,
          maxTicksLimit: 10,
          font: { family: CHART_FONT, size: 11 },
          color: "#000",
        },
        grid: {
          color: GRID_COLOR,
          lineWidth: 1,
          drawOnChartArea: true,
          drawTicks: false,
        },
        border: {
          color: "#000",
          width: 1,
        },
      },
    },
    elements: {
      line: {
        borderWidth: 1.5,
        tension: 0.4,
      },
      point: {
        hitRadius: 8,
      },
    },
  };

  const pointLabel = (x, y, name) => {
    const label = `${x}L/min @ ${y}kPa`;
    return name ? `${label}` : label;
  };

  const lineDataset = (label, points, color) => ({
    label,
    data: points,
    fill: false,
    borderColor: color,
    backgroundColor: color,
    borderWidth: 1.5,
    tension: 0.4,
    cubicInterpolationMode: "monotone",
    pointRadius: 0,
    pointHoverRadius: 4,
    datalabels: { display: false },
  });

  const markerDataset = (label, points, color) => ({
    label,
    data: points,
    showLine: false,
    fill: false,
    borderColor: color,
    backgroundColor: color,
    pointStyle: "rect",
    pointRadius: 4,
    pointHoverRadius: 5,
    borderWidth: 0,
    datalabels: {
      display: text,
      align: "right",
      anchor: "center",
      offset: 8,
      color: "#000",
      font: { family: CHART_FONT, size: 10, weight: "normal" },
      formatter: (value) => pointLabel(value.x, value.y, value.name),
    },
  });

  const getDataSets = () => {
    const datasets = [];

    const townMainPoints = [...townMain]
      .map((item) => ({
        x: Number(item.sprinkler_flow),
        y: Number(item.discharge),
      }))
      .filter((p) => !Number.isNaN(p.x) && !Number.isNaN(p.y))
      .sort((a, b) => a.x - b.x);

    if (townMainPoints.length > 0) {
      datasets.push(lineDataset("Town Main", townMainPoints, TOWN_MAIN_COLOR));
    }

    const hydrantPoints = [...hydrantData]
      .map((item) => ({
        x: Number(item.hydrant_flow),
        y: Number(item.hydrant_test_location),
      }))
      .filter((p) => !Number.isNaN(p.x) && !Number.isNaN(p.y))
      .sort((a, b) => a.x - b.x);

    if (hydrantPoints.length > 0) {
      datasets.push(lineDataset("Hydrants", hydrantPoints, HYDRANT_COLOR));
    }

    const sprinklerPoints = sprinklersData
      .map((item) => ({
        x: Number(item.demand_flow),
        y: Number(item.demand_pressure),
        name: item.area_protected || "",
      }))
      .filter((p) => !Number.isNaN(p.x) && !Number.isNaN(p.y));

    if (sprinklerPoints.length > 0) {
      datasets.push(markerDataset("Sprinkler", sprinklerPoints, SPRINKLER_COLOR));
    }

    return datasets;
  };

  const data = {
    datasets: getDataSets(),
  };

  const get = async () => {
    try {
      const { data } = await axios.get(`/assesment/${state.id}`);
      if (!data?.town_main?.town_mains_check) {
        setTownMain(data?.town_main?.town_mains || []);
      } else {
        setTownMain([]);
      }
      if (!data?.town_main?.hydrant_check) {
        setHydrantData(data?.town_main?.hydrant || []);
      } else {
        setHydrantData([]);
      }
      setSprinklersData(data?.hydrant_system?.hydrant || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    get();
    // eslint-disable-next-line
  }, []);

  return (
    <div>
      <Grid
        container
        xs={12}
        sx={{
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          alignContent: "flex-start",
        }}
      >
        <Grid container item xs={12}>
          <div
            style={{
              backgroundColor: "#ffffff",
              padding: CHART_PADDING_PX,
              width: HYDRANT_CHART_WIDTH_PX,
              height: HYDRANT_CHART_HEIGHT_PX,
              minWidth: HYDRANT_CHART_WIDTH_PX,
              maxWidth: HYDRANT_CHART_WIDTH_PX,
              minHeight: HYDRANT_CHART_HEIGHT_PX,
              maxHeight: HYDRANT_CHART_HEIGHT_PX,
              boxSizing: "border-box",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: "#ffffff",
                border: "1px solid #000",
                boxSizing: "border-box",
                position: "relative",
              }}
            >
              <Line
                options={options}
                data={data}
                width={CHART_INNER_WIDTH_PX}
                height={CHART_INNER_HEIGHT_PX}
              />
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
}
